import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";

import PlatformAppBar from "../../../components/layout/PlatformAppBar";
import PlatformFooter from "../../../components/layout/PlatformFooter";
import LenderPlatformSidebar from "../../../components/layout/lender/PlatformSidebar";

import "./LenderLayout.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const DASHBOARD_REFRESH_INTERVAL_MS = 20 * 60 * 1000;

export default function LenderLayout() {
  const { t } = useTranslation("lender-account");
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const navigate = useNavigate();

  const [formDirty, setFormDirty] = React.useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = React.useState("");
  const [leaveModalOpen, setLeaveModalOpen] = React.useState(false);

  const [me, setMe] = React.useState(null);
  const [isLoadingMe, setIsLoadingMe] = React.useState(true);
  const [meError, setMeError] = React.useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const lastLoadedAtRef = React.useRef(0);
  const discardChangesHandlerRef = React.useRef(null);

  /* =========================================================
     REGISTER DISCARD CHANGES HANDLER
     ========================================================= */

  const registerDiscardChangesHandler = React.useCallback((handler) => {
    discardChangesHandlerRef.current =
      typeof handler === "function" ? handler : null;

    return () => {
      if (discardChangesHandlerRef.current === handler) {
        discardChangesHandlerRef.current = null;
      }
    };
  }, []);

  /* =========================================================
     LOAD LENDER DATA
     ========================================================= */

  const loadMe = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!isAuthenticated) {
        setMe(null);
        setIsLoadingMe(false);
        return;
      }

      try {
        if (!silent) {
          setIsLoadingMe(true);
        }

        setMeError("");

        const token = await getAccessTokenSilently();

        const res = await fetch(`${API_BASE}/api/lender/me/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          console.error("Failed to load /api/lender/me/", data);

          setMeError("layout.loadError");
          setMe(null);
          return;
        }

        if (data?.linked === false) {
          setMe(null);
          setMeError("");
          return;
        }

        setMe(data);
        lastLoadedAtRef.current = Date.now();
      } catch (error) {
        console.error("Failed to load /api/lender/me/", error);

        setMeError("layout.loadError");
        setMe(null);
      } finally {
        if (!silent) {
          setIsLoadingMe(false);
        }
      }
    },
    [isAuthenticated, getAccessTokenSilently],
  );

  React.useEffect(() => {
    loadMe();
  }, [loadMe]);

  /* =========================================================
     PERIODIC DASHBOARD REFRESH
     ========================================================= */

  React.useEffect(() => {
    if (!isAuthenticated) return undefined;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadMe({ silent: true });
      }
    }, DASHBOARD_REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      const now = Date.now();
      const msSinceLastLoad = now - lastLoadedAtRef.current;

      if (msSinceLastLoad >= DASHBOARD_REFRESH_INTERVAL_MS) {
        loadMe({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, loadMe]);

  /* =========================================================
     ACCOUNT DATA
     ========================================================= */

  const account = me?.account || {};
  const profile = me?.profile || {};
  const organisation = me?.organisation || {};
  const dashboardSummary = me?.dashboard_summary ?? null;

  const fullName =
    account.full_name ||
    `${account.first_name || ""} ${account.last_name || ""}`.trim() ||
    t("layout.defaultUser");

  const initials =
    account.initials ||
    `${account.first_name?.[0] || ""}${account.last_name?.[0] || ""}`.toUpperCase() ||
    t("layout.defaultInitials");

  const marketRegions =
    me?.profile?.market_regions || dashboardSummary?.market_regions || [];

  /* =========================================================
     PROTECTED NAVIGATION
     ========================================================= */

  const requestNavigation = React.useCallback(
    (nextPath, navigationOptions = {}) => {
      if (!nextPath) return;

      if (!formDirty) {
        navigate(nextPath, navigationOptions);
        return;
      }

      setPendingNavigationPath(nextPath);
      setLeaveModalOpen(true);
    },
    [formDirty, navigate],
  );

  function cancelProtectedNavigation() {
    setLeaveModalOpen(false);
    setPendingNavigationPath("");
  }

  function confirmProtectedNavigation() {
    const nextPath = pendingNavigationPath;

    discardChangesHandlerRef.current?.();
    discardChangesHandlerRef.current = null;

    setFormDirty(false);
    setLeaveModalOpen(false);
    setPendingNavigationPath("");

    if (nextPath) {
      navigate(nextPath);
    }
  }

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="lender-dashboard-page">
      <PlatformAppBar
        actorType={account?.primary_actor}
        fullName={fullName}
        initials={initials}
        notificationCount={3}
        homePath="/"
        onOpenMobileMenu={() => setMobileMenuOpen(true)}
        avatarImageUrl={account.image_url || organisation.logo_url || ""}
        avatarFit="contain"
        notificationsEnabled={
          isAuthenticated && !isLoadingMe && Boolean(me?.account)
        }
      />

      <div className="lender-dashboard-main">
        <LenderPlatformSidebar
          mobileOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          dashboardSummary={dashboardSummary}
          onNavigate={requestNavigation}
        />

        <div className="lender-dashboard-content-shell">
          {!me && isLoadingMe ? (
            <div className="lender-layout-loading">
              <div className="lender-layout-loading__card">
                {t("layout.loading")}
              </div>
            </div>
          ) : meError && !me ? (
            <div className="lender-layout-loading">
              <div className="lender-layout-loading__card lender-layout-loading__card--error">
                {t(meError)}
              </div>
            </div>
          ) : (
            <Outlet
              context={{
                me,
                account,
                profile,
                organisation,
                dashboardSummary,
                marketRegions,
                refreshMe: loadMe,
                isLoadingMe,

                formDirty,
                setFormDirty,
                requestNavigation,
                registerDiscardChangesHandler,
              }}
            />
          )}
        </div>
      </div>

      {/* =====================================================
          UNSAVED CHANGES CONFIRMATION MODAL
          ===================================================== */}

      {leaveModalOpen ? (
        <div
          className="lender-layout-leave-overlay"
          onMouseDown={cancelProtectedNavigation}
        >
          <div
            className="lender-layout-leave-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="lender-layout-leave-title"
            aria-describedby="lender-layout-leave-description"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="lender-layout-leave-modal__close"
              onClick={cancelProtectedNavigation}
              aria-label={t("layout.close")}
            >
              <X size={17} />
            </button>

            <div className="lender-layout-leave-modal__heading">
              <div className="lender-layout-leave-modal__icon">
                <AlertCircle size={22} />
              </div>

              <div>
                <h3 id="lender-layout-leave-title">{t("layout.leaveTitle")}</h3>

                <p id="lender-layout-leave-description">
                  {t("layout.leaveDescription")}
                </p>
              </div>
            </div>

            <div className="lender-layout-leave-modal__actions">
              <button
                type="button"
                className="lender-layout-leave-modal__stay"
                onClick={cancelProtectedNavigation}
              >
                {t("layout.stay")}
              </button>

              <button
                type="button"
                className="lender-layout-leave-modal__leave"
                onClick={confirmProtectedNavigation}
              >
                {t("layout.leave")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PlatformFooter />
    </div>
  );
}
